// useMentorAdvice — shared hook for "has a recruited mentor got fresh advice
// this week?" Used by both the Mastermind meeting (the recipient) and the
// world's Mentor's Cottage glow (the trigger). Single source of truth for the
// signal computation so the glow and the meeting can never disagree.

import { computeMentorSignals, type MentorId, type Txn } from '@biz-kid/engine';
import { useGameState } from '@biz-kid/store';
import { useLife, useLifeComputed } from './store.js';
import { selectAdvice } from './mentorAdvice.js';

const weekCashflow = (ledger: Txn[], w: number): number =>
  ledger.filter((t) => t.week === w).reduce((s, t) => (t.category === 'capital' ? s : s + t.amount), 0);

export type MentorAdviceState = {
  /** Mentors who would speak this week, with their line. */
  speakingCount: number;
  /** True when there's fresh advice the player hasn't seen this week.
   *  Drives the glow chip on the world's Mentor's Cottage. */
  hasFreshAdvice: boolean;
};

export function useMentorAdvice(): MentorAdviceState {
  const life = useLife();
  const computed = useLifeComputed();
  const week = useGameState((s) => s.clock.week);
  const ledger = useGameState((s) => s.ledger);

  const lastWeekCashflow = week > 1 ? weekCashflow(ledger, week - 1) : null;
  const savingsValue = life.assets.find((a) => a.type === 'savings')?.value ?? 0;

  const signals = computeMentorSignals({
    savingsValue,
    cash: life.cash,
    persistence: life.mindset.persistence,
    freedomPct: computed.freedomPct,
    achievedDreams: life.visionBoard.filter((v) => v.achieved).length,
    lastWeekCashflow,
    totalAssetValue: computed.totalAssetValue,
    cashIdleYears: life.cashIdleYears ?? 0,
  });

  const speakingCount = life.mastermind.reduce((count, m) => {
    const line = selectAdvice(m.id as MentorId, signals, week);
    return count + (line ? 1 : 0);
  }, 0);

  const lastRead = life.lastMentorReadWeek ?? 0;
  const hasFreshAdvice = speakingCount > 0 && lastRead < week;

  return { speakingCount, hasFreshAdvice };
}
